import React, { useEffect, useState } from 'react';
import { Popup } from 'antd-mobile';
import config from '@/pages/Web/Outside/pages/Simulation/Pages/components/Evaluation/evaluation.config.json';
import { handleSubmitEvaluation } from '@/api/grt/evaluation';
import { SUGGESTION_CODE } from '@/pages/hooks/CaseSImulation/useEvaluation';
import ToastUtils from '@/utils/ToastUtils';
import StarRateRow from './components/StarRateRow';
import TextQuestionRow from './components/TextQuestionRow';
import s from './style.module.scss';
// 弹窗标题装饰图（"我要评价"艺术字，与 PC 共用素材）
const titleIcon = require('@/assets/images/simulation/evaluationTool/title_icon.webp');

/**
 * 案例仿真评价弹窗（H5 版）：底部半屏面板，UI 独立于 PC 版实现。
 * 题目渲染/必选校验/提交报文与 PC 保持同一套约定，均由题库 JSON（evaluation.config.json）驱动。
 * @param {boolean}  open       是否展示
 * @param {string}   scene      场景语义 key（caseSimulation）
 * @param {object}   params     业务身份参数（useEvaluation.getEvaluateParams()）
 * @param {function} onClose    关闭回调（暂不评价/提交成功后关闭）
 * @param {function} onSuccess  提交成功回调（父级切换已评价态）
 */
const EvaluationModal = (props) => {
    const { open, scene, params = {}, onClose, onSuccess } = props,
        sceneConfig = config.scenes[scene] || {},
        questions = sceneConfig.questions || [],
        [answers, setAnswers] = useState({}),
        [submitting, setSubmitting] = useState(false);

    // 必答完成度：所有非文本题均已选择（题目量级小，普通派生值即可，无需 useMemo）
    const allRequiredAnswered = questions.filter((q) => q.type !== 'text').every((q) => answers[q.code] !== undefined);

    const onAnswer = (code, value) => {
        setAnswers((prev) => ({ ...prev, [code]: value }));
    };

    // 每次打开重置表单
    useEffect(() => {
        if (open) {
            setAnswers({});
            setSubmitting(false);
        }
    }, [open]);

    const onSubmit = async () => {
        if (submitting) return;
        // 未完成所有题目作答 → toast"请完成所有评价项"，弹窗保持
        if (!allRequiredAnswered) {
            ToastUtils.showToast('请完成所有评价项');
            return;
        }
        setSubmitting(true);
        try {
            const dimension_scores = questions
                .filter((q) => q.type !== 'text')
                .map((q) => ({ dimension_code: q.code, score: answers[q.code] }));
            const requestBody = {
                scene: sceneConfig.scene,
                ...params,
                dimension_scores
            };
            const suggestion = answers[SUGGESTION_CODE];
            if (suggestion) {
                requestBody.suggestion = suggestion;
            }
            const res = await handleSubmitEvaluation(requestBody);
            if (res?.code === '00000') {
                ToastUtils.showToast('感谢您的评价！');
                onSuccess?.();
                onClose?.();
            } else {
                ToastUtils.showAbnormalToast(res?.msg || '评价提交失败，请重试');
            }
        } catch (error) {
            console.error(error, '提交评价失败');
            ToastUtils.showAbnormalToast('评价提交失败，请重试');
        } finally {
            setSubmitting(false);
        }
    };

    const renderQuestion = (question) => {
        const commonProps = {
            question,
            value: answers[question.code],
            onChange: onAnswer
        };
        switch (question.type) {
            case 'star':
                return <StarRateRow key={question.code} {...commonProps} />;
            case 'text':
                return <TextQuestionRow key={question.code} {...commonProps} />;
            default:
                return null;
        }
    };

    return (
        <Popup
            visible={open}
            position="bottom"
            destroyOnClose={true}
            onMaskClick={onClose}
            onClose={onClose}
            bodyClassName={s['evaluation-popup-body']}
        >
            <div className={s['sheet-wrap']}>
                <div className={s['evaluation-sheet']}>
                    <div className={s['sheet-header']}>
                        <div className={s['header-bar']}>
                            <img className={s['title-icon']} src={titleIcon} alt={sceneConfig.title} />
                            <span className={s['skip-btn']} onClick={onClose}>
                                {sceneConfig.skipText}
                            </span>
                        </div>
                        <div className={s['header-tip']}>{sceneConfig.header}</div>
                    </div>
                    <div className={s['sheet-content']}>
                        <div className={s['question-list']}>{questions.map(renderQuestion)}</div>
                        {/* 按钮始终高亮可点：未完成时点击 toast"请完成所有评价项"（对齐参考版，无置灰态） */}
                        <button
                            className={s['submit-btn']}
                            disabled={submitting}
                            onClick={onSubmit}
                        >
                            {submitting ? '提交中…' : sceneConfig.submitText}
                        </button>
                    </div>
                </div>
            </div>
        </Popup>
    );
};

export default EvaluationModal;
